import React, { useCallback, useEffect, useMemo, useState } from "react";
import { connect } from "react-redux";
import { Link } from "react-router-dom";
import {
  Box,
  Button,
  Checkbox,
  FormControl,
  FormControlLabel,
  Grid,
  MenuItem,
  Select,
  TextField,
  Typography,
} from "@mui/material";
import { toast } from "react-toastify";
import Hoc from "../../component/HOC";
import FormInput from "../ProgramSubscriptions/FormInput";
import RichTextEditor from "../ProgramSubscriptions/TextEditor";
import "../ProgramSubscriptions/Program.scss";
import {
  getEmailTemplates,
  saveEmailTemplate,
  testSendEmailTemplate,
  showDetail,
} from "../../redux/action/user";

const decodeHtmlEntitiesToHtml = (input) => {
  const str = String(input ?? "");
  if (!str) return "";
  if (!str.includes("&lt;") && !str.includes("&gt;") && !str.includes("&amp;"))
    return str;
  const textarea = document.createElement("textarea");
  textarea.innerHTML = str;
  return textarea.value;
};

const MergeTagsReference = ({ mergeTags, globalMergeTags }) => {
  const tags = mergeTags ?? [];
  const globals = globalMergeTags ?? [];

  const renderList = (rows) => (
    <Box
      component="ul"
      sx={{ m: 0, pl: 2.5, "& li": { mb: 0.75 } }}
    >
      {rows.map((row) => (
        <Box component="li" key={row.tag}>
          <Typography
            component="span"
            sx={{
              fontFamily: "monospace",
              fontSize: "0.85rem",
              color: "#fff",
              fontWeight: 600,
            }}
          >
            {row.tag}
          </Typography>
          {row.description ? (
            <Typography
              component="span"
              variant="body2"
              sx={{ color: "rgba(255,255,255,0.7)", ml: 1 }}
            >
              — {row.description}
            </Typography>
          ) : null}
        </Box>
      ))}
    </Box>
  );

  return (
    <Box
      sx={{
        px: 1,
        py: 1.5,
        mb: 1,
        borderRadius: 1,
        border: "1px solid rgb(39, 51, 52)",
        backgroundColor: "rgb(22, 34, 36)",
      }}
    >
      <Typography
        variant="subtitle2"
        sx={{ color: "#0bd7ee", mb: 0.5, textTransform: "uppercase", fontSize: "0.75rem" }}
      >
        Available merge tags
      </Typography>
      <Typography variant="body2" sx={{ color: "rgba(255,255,255,0.75)", mb: 1 }}>
        Insert these tags in the subject or body. The server replaces them when the email is sent.
      </Typography>
      <Box sx={{ maxHeight: 220, overflowY: "auto" }}>
        {tags.length > 0 ? renderList(tags) : null}
        {globals.length > 0 ? (
          <>
            <Typography
              variant="body2"
              sx={{ color: "rgba(255,255,255,0.5)", mt: 1, mb: 0.5 }}
            >
              Global tags (available in every email):
            </Typography>
            {renderList(globals)}
          </>
        ) : null}
      </Box>
    </Box>
  );
};

const buildFormsFromPayload = (payload) => {
  const forms = {};
  const templates = payload?.templates ?? [];
  templates.forEach((t) => {
    forms[t.emailKey] = {
      id: t.id != null ? String(t.id) : "",
      emailKey: t.emailKey,
      name: t.name,
      description: t.description,
      emailSubject: t.emailSubject || "",
      bodyHtml: decodeHtmlEntitiesToHtml(t.bodyHtml || ""),
      isActive: t.isActive !== false,
      mergeTags: t.mergeTags || [],
    };
  });
  return forms;
};

const EmailTemplatesForm = (props) => {
  const [forms, setForms] = useState({});
  const [selectedKey, setSelectedKey] = useState("");
  const [testEmail, setTestEmail] = useState("");
  const [sending, setSending] = useState(false);

  useEffect(() => {
    props.showDetail(true);
    props.getEmailTemplates();
    return () => props.showDetail(false);
  }, []);

  useEffect(() => {
    const payload = props.emailTemplates;
    if (!payload) return;
    const built = buildFormsFromPayload(payload);
    setForms(built);
    setSelectedKey((prev) => prev || (payload.templates?.[0]?.emailKey ?? ""));
  }, [props.emailTemplates]);

  const templateOptions = useMemo(
    () => props.emailTemplates?.templates ?? [],
    [props.emailTemplates]
  );
  const globalMergeTags = props.emailTemplates?.globalMergeTags ?? [];
  const current = selectedKey ? forms[selectedKey] : null;

  const handleField = useCallback(
    (field) => (e) => {
      const value = e.target.value;
      setForms((prev) => ({
        ...prev,
        [selectedKey]: { ...prev[selectedKey], [field]: value },
      }));
    },
    [selectedKey]
  );

  const handleBody = useCallback(
    (_, content) => {
      setForms((prev) => ({
        ...prev,
        [selectedKey]: { ...prev[selectedKey], bodyHtml: content },
      }));
    },
    [selectedKey]
  );

  const handleActive = useCallback(
    (e) => {
      const checked = e.target.checked;
      setForms((prev) => ({
        ...prev,
        [selectedKey]: { ...prev[selectedKey], isActive: checked },
      }));
    },
    [selectedKey]
  );

  const submit = async () => {
    if (!current) return;
    if (!current.emailSubject?.trim()) {
      toast.error("Email subject is required");
      return;
    }
    const payload = new URLSearchParams();
    payload.append("email_key", current.emailKey);
    payload.append("email_subject", current.emailSubject || "");
    payload.append("body_html", current.bodyHtml || "");
    payload.append("is_active", current.isActive ? "1" : "0");

    const res = await props.saveEmailTemplate(payload);
    if (res && res.status === 200) {
      toast.success(res.data?.message || "Email template saved");
      props.getEmailTemplates();
    } else {
      toast.error(
        res?.response?.data?.message || res?.message || "Something went wrong"
      );
    }
  };

  const sendTest = async () => {
    if (!current) return;
    if (!testEmail?.trim()) {
      toast.error("Enter a recipient email for the test");
      return;
    }
    setSending(true);
    const res = await props.testSendEmailTemplate({
      email_key: current.emailKey,
      to: testEmail.trim(),
    });
    setSending(false);
    if (res && res.status === 200) {
      toast.success(res.data?.message || "Test email triggered");
    } else {
      toast.error(
        res?.response?.data?.message || res?.message || "Failed to send test email"
      );
    }
  };

  return (
    <Box sx={pageWrap}>
      <Hoc activeMenu="Email Templates" subTitle="Transactional emails">
        <Box className="container-fluid p-0 mt-4" sx={panelWrap}>
          <Box
            sx={{
              px: 2,
              pt: 2,
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              flexWrap: "wrap",
              gap: 1,
            }}
          >
            <FormControl sx={{ minWidth: 320 }} size="small">
              <Select
                value={selectedKey}
                displayEmpty
                onChange={(e) => setSelectedKey(e.target.value)}
                sx={{
                  backgroundColor: "#1d2b2c",
                  color: "#fff",
                  "& .MuiSvgIcon-root": { color: "#fff" },
                }}
              >
                {templateOptions.length === 0 ? (
                  <MenuItem value="">
                    {props.emailTemplatesLoading ? "Loading…" : "No templates"}
                  </MenuItem>
                ) : (
                  templateOptions.map((t) => (
                    <MenuItem key={t.emailKey} value={t.emailKey}>
                      {t.name}
                    </MenuItem>
                  ))
                )}
              </Select>
            </FormControl>

            <Link
              to="/email-templates/purchase-receipts"
              style={{ color: "#0bd7ee", textDecoration: "none", fontSize: "0.9rem" }}
            >
              Manage purchase receipt emails →
            </Link>
          </Box>

          {current ? (
            <Grid container spacing={2} className="FieldsWrap" sx={{ px: 1, mt: 0 }}>
              <Grid item xs={12} md={12}>
                {current.description ? (
                  <Typography
                    variant="body2"
                    sx={{ color: "rgba(255,255,255,0.7)", px: 2, pb: 1 }}
                  >
                    {current.description}
                  </Typography>
                ) : null}

                <Box sx={{ px: 1, py: 1 }}>
                  <MergeTagsReference
                    mergeTags={current.mergeTags}
                    globalMergeTags={globalMergeTags}
                  />
                </Box>

                <FormInput
                  label="Email subject"
                  value={current.emailSubject}
                  onChange={handleField("emailSubject")}
                  placeholder="e.g. Your Japa Health OTP: {{otp}}"
                />

                <Box
                  sx={{
                    backgroundColor: "transparent",
                    padding: "8px",
                    display: "flex",
                    alignItems: "center",
                    borderBottom: "1px solid rgb(89 89 89 / 17%)",
                  }}
                >
                  <Typography
                    sx={{
                      color: "#fff",
                      width: "13%",
                      marginRight: "8px",
                      fontSize: "0.8rem",
                      textTransform: "uppercase",
                    }}
                  >
                    Status:
                  </Typography>
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={current.isActive}
                        onChange={handleActive}
                        color="primary"
                        sx={{
                          color: "#fff",
                          "& .MuiSvgIcon-root": {
                            border: "1px solid #000",
                            borderRadius: "2px",
                          },
                        }}
                      />
                    }
                    label={
                      <Typography sx={{ color: "#fff", fontSize: "0.9rem" }}>
                        Active (when unchecked, the built-in default template is used)
                      </Typography>
                    }
                  />
                </Box>

                <Box
                  sx={{
                    backgroundColor: "transparent",
                    padding: "8px",
                    borderBottom: "1px solid rgb(89 89 89 / 17%)",
                  }}
                >
                  <label
                    style={{
                      display: "block",
                      color: "#fff",
                      width: "13%",
                      marginBottom: "8px",
                      fontSize: "0.8rem",
                      textTransform: "uppercase",
                    }}
                  >
                    Email body:
                  </label>
                  <Box sx={{ width: "100%", backgroundColor: "#fff", borderRadius: 1 }}>
                    <RichTextEditor
                      key={selectedKey}
                      index={0}
                      value={current.bodyHtml}
                      onChange={handleBody}
                      rawHtml
                    />
                  </Box>
                </Box>

                <Box
                  sx={{
                    px: 2,
                    pb: 2,
                    pt: 1,
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    flexWrap: "wrap",
                    gap: 1,
                  }}
                >
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                    <TextField
                      size="small"
                      value={testEmail}
                      onChange={(e) => setTestEmail(e.target.value)}
                      placeholder="test@example.com"
                      sx={{
                        minWidth: 240,
                        backgroundColor: "#fff",
                        borderRadius: "6px",
                        border: "1px solid #c4c4c4",
                        "&:focus-within": { borderColor: "#0bd7ee" },
                        "& .MuiOutlinedInput-notchedOutline": { border: "none" },
                        "& .MuiOutlinedInput-input": { color: "#000", padding: "8px 12px" },
                        "& input::placeholder": { color: "#666", opacity: 1 },
                      }}
                    />
                    <Button
                      variant="outlined"
                      onClick={sendTest}
                      disabled={sending}
                      sx={{ color: "#0bd7ee", borderColor: "#0bd7ee", textTransform: "none" }}
                    >
                      Send test
                    </Button>
                  </Box>

                  <Button
                    variant="contained"
                    onClick={submit}
                    disabled={props.saveEmailTemplateLoading || props.emailTemplatesLoading}
                    sx={{
                      backgroundColor: "#0bd7ee",
                      color: "#000",
                      textTransform: "none",
                    }}
                  >
                    Save template
                  </Button>
                </Box>
              </Grid>
            </Grid>
          ) : (
            <Box sx={{ p: 3, color: "rgba(255,255,255,0.6)" }}>
              {props.emailTemplatesLoading ? "Loading templates…" : "No template selected."}
            </Box>
          )}
        </Box>
      </Hoc>
    </Box>
  );
};

const pageWrap = {
  ".menu-item-container": {
    "& p": {
      color: "#fff",
      opacity: 1,
    },
  },
};

const panelWrap = {
  boxShadow:
    "rgba(0, 0, 0, 0.2) 0px 2px 1px -1px, rgba(0, 0, 0, 0.14) 0px 1px 1px 0px, rgba(0, 0, 0, 0.12) 0px 1px 3px 0px",
  width: "100%",
  overflowX: "auto",
  backgroundColor: "rgb(15, 28, 30)",
  border: "1px solid rgb(39, 51, 52)",
  color: "rgb(255, 255, 255)",
  margin: "10px 20px",
};

const actionCreators = {
  showDetail,
  getEmailTemplates,
  saveEmailTemplate,
  testSendEmailTemplate,
};

export default connect(
  ({ user }) => ({
    emailTemplates: user.emailTemplates,
    emailTemplatesLoading: user.emailTemplatesLoading,
    saveEmailTemplateLoading: user.saveEmailTemplateLoading,
  }),
  actionCreators
)(EmailTemplatesForm);
